/**
 * Section 14 — Broker Setup.
 *
 * Practical guide to connecting Delta Exchange and Zerodha. IP whitelist
 * for Delta, OAuth flow for Zerodha, daily-token-expiry handling,
 * Test Connection workflow.
 */
import React from 'react';
import { Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, List, ListItem, ListItemText } from '@mui/material';
import KeyIcon from '@mui/icons-material/Key';
import GuideCallout from '../GuideCallout';
import GuideExample from '../GuideExample';
import GuideCodeBlock from '../GuideCodeBlock';
import OpenInApp from '../OpenInApp';
import { GuideSection } from './types';

const H = ({ id, children }: { id: string; children: React.ReactNode }) => (
  <Typography id={`anchor-${id}`} variant="h5" sx={{ fontWeight: 700, mt: 4, mb: 1.25, scrollMarginTop: 80 }}>{children}</Typography>
);
const H3 = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2.5, mb: 0.75 }}>{children}</Typography>
);
const P = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body1" sx={{ mb: 1.25, lineHeight: 1.65, fontSize: '0.95rem' }}>{children}</Typography>
);

const Section_broker_setup: GuideSection = {
  id: 'broker-setup',
  label: 'Broker Setup',
  group: 'setup',
  icon: <KeyIcon fontSize="small" />,
  render: () => (
    <>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>Broker Setup</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Step-by-step for the two supported brokers. Both are independent — you can use either or both.
        Delta for crypto perpetuals, Zerodha for Indian equity and F&O.
      </Typography>

      <GuideCallout kind="info" title="Encrypted at rest">
        Both apiKey and apiSecret are AES-256-GCM encrypted in the database (since R80). Even an admin
        reading your row sees a ciphertext. The secret never leaves the server in plaintext.
      </GuideCallout>

      <H id="delta">Delta Exchange</H>
      <H3>Generate API credentials at Delta</H3>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<>Log in to Delta Exchange India web console (https://www.delta.exchange/app/account).</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary="Account → API → Create New API Key. Pick permissions: enable Trading + Read (do NOT enable Withdrawal)." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<>Critical: in the IP Whitelist field add <strong>167.86.119.214</strong> — earngush's outbound IP.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary="Click Save. Delta shows you the API key + secret once. Copy both into a password manager — Delta never shows the secret again." />
        </ListItem>
      </List>

      <H3>Save in earngush</H3>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<>Open <OpenInApp to="/settings" label="Settings → Broker Credentials" />.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary="Click Add → broker type Delta Exchange." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary="Paste the API key + secret. Click Save." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<>Click <strong>Test Connection</strong>. Within ~1 second you should see a green check + your account balance summary.</>} />
        </ListItem>
      </List>

      <GuideCallout kind="warning" title="ip_not_whitelisted_for_api_key">
        The most common Delta error. earngush's IP must be in Delta's allow-list. If you see this error in
        the Logs / dashboard banner, go back to Delta and add 167.86.119.214 to your API key's whitelist.
        Re-running Test Connection within 30 seconds usually clears it.
      </GuideCallout>

      <H3>What earngush does with the Delta credentials</H3>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="Polls /v2/trades every 5 seconds per tracked symbol to build the Order Flow aggregator." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="Pulls candle history on chart loads." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="Places, modifies, and cancels orders when you Execute alerts or use chart triggers." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="Pulls position state every 10 seconds for the Trades page." />
        </ListItem>
      </List>

      <H id="zerodha">Zerodha</H>
      <P>
        Zerodha uses OAuth. You provide the API key + secret <em>and</em> you need to complete the login
        flow at the start of every trading day (Zerodha access tokens expire daily at ~06:00 IST).
      </P>

      <H3>Generate API credentials at Kite Connect</H3>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<>Go to https://kite.trade and sign up for a Kite Connect developer account (₹2,000/month).</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary="Create a new app. App type: Connect." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<>Redirect URL: <code>https://earngush.com/zerodha-callback</code> (exact, no trailing slash).</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary="Save. Copy the API key + API secret." />
        </ListItem>
      </List>

      <H3>Save in earngush + complete OAuth</H3>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<>Settings → Broker Credentials → Add → broker type Zerodha.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary="Paste API key + API secret. Save." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<>Click <strong>Login with Zerodha</strong>. You're redirected to kite.zerodha.com → enter your trading login + TOTP from your authenticator app.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<>On success, kite bounces you back to <code>/zerodha-callback</code> with a <code>request_token</code>. earngush exchanges this for an access token (server-side) and stores it encrypted.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary="Click Test Connection — green check + balance summary confirms wiring." />
        </ListItem>
      </List>

      <GuideCallout kind="warning" title="Daily token expiry">
        Zerodha access tokens expire at ~06:00 IST every day. Each morning your first attempt to place an
        order via Zerodha will fail with <code>TokenException</code> until you re-login. Either log in
        manually via Settings → Broker Credentials → Login with Zerodha, or use the
        <OpenInApp to="/settings" label="Settings → Scheduled Tasks" /> automation to schedule a
        reminder / auto-login (you'll still need to type the TOTP).
      </GuideCallout>

      <H3>What earngush does with the Zerodha credentials</H3>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="Fetches account balance + holdings on demand." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="Pulls per-symbol live LTP for the dashboard + chart auto-refresh." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="Imports NSE/BSE/NFO instrument master daily (or on-demand via Instruments → Import Zerodha)." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="Places orders via /orders/regular. SL / Target follow-on orders piggy-back as separate regular orders." />
        </ListItem>
      </List>

      <H id="multiple-brokers">Using both brokers at once</H>
      <P>
        earngush routes orders by the alert's symbol exchange. An alert for BTCUSD goes to Delta; an alert
        for NIFTY goes to Zerodha. No configuration needed once both credentials are saved.
      </P>
      <P>
        The Dashboard aggregates portfolio value across both brokers (cash + open positions in INR — Delta
        balances are converted from USDT using the configured USD→INR rate). The breakdown is visible per-
        broker in the Portfolio Value tile's tooltip.
      </P>

      <H id="rotation">Rotating credentials</H>
      <P>
        Periodic rotation is good hygiene. The flow:
      </P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary="Generate a new API key + secret in the broker's web console (don't revoke the old one yet)." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary="In Settings → Broker Credentials, edit the row and paste the new values. Save." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<>Test Connection. Green check → revoke the old key in the broker's console.</>} />
        </ListItem>
      </List>

      <GuideExample title="A typical morning routine (both brokers)">
        06:15 — Dashboard banner shows "Zerodha access token expired" (red).<br />
        06:16 — Settings → Broker Credentials → Login with Zerodha → kite OAuth + TOTP → bounce back.<br />
        06:17 — Test Connection on Zerodha row → green ✓. Banner clears.<br />
        09:15 — Indian markets open; auto-scans + alerts flow. Crypto's been running 24/7, no morning task.
      </GuideExample>
    </>
  ),
  searchIndex: [
    { heading: 'Delta Exchange',           anchor: 'delta',         body: 'delta exchange api key secret ip whitelist crypto perpetual' },
    { heading: 'Zerodha',                  anchor: 'zerodha',       body: 'zerodha kite connect oauth access token daily expiry' },
    { heading: 'Using both brokers',       anchor: 'multiple-brokers', body: 'multiple brokers routing exchange portfolio' },
    { heading: 'Rotating credentials',     anchor: 'rotation',      body: 'rotating credentials new api key revoke' },
  ],
};

export default Section_broker_setup;
